import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Building2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../components/SearchBar";
import SortableTable from "../components/SortableTable";
import PaginationControls from "../components/PaginationControls";
import ExportButton from "../components/ExportButton";
import Button from "../../../common/Button";
import { groupRoles, SERVICE_MANAGEMENT_ENABLED } from "../roleUtils";
import { getActiveCompanyOptions } from "./AccessGroupsList";
import {
  getSelectedCompanyUuid,
  setSelectedCompanyUuid as saveSelectedCompanyUuid,
} from "../../../../lib/local/SelectedCompany";
import "./RolesList.css";

export default function RolesList({ onRoleSelect, onAddRole }) {
  const { roles, companies, fetchRoles, deleteRole } = useContext(
    FieldConfigurationContext
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 10,
  });

  // Fetch roles on mount
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const companyOptions = useMemo(
    () => getActiveCompanyOptions(companies, companies.configureRolesData),
    [companies]
  );
  const isMultiCompany = companyOptions.length > 1;

  // Apply the shared company selection once the options are available.
  const appliedStoredCompany = useRef(false);
  useEffect(() => {
    if (appliedStoredCompany.current || !isMultiCompany) return;
    appliedStoredCompany.current = true;
    const stored = getSelectedCompanyUuid();
    if (stored && companyOptions.some((opt) => opt.value === stored)) {
      setSelectedCompanyUuid(stored);
    }
  }, [isMultiCompany, companyOptions]);

  const handleCompanyChange = (e) => {
    setSelectedCompanyUuid(e.target.value);
    saveSelectedCompanyUuid(e.target.value);
  };

  // Filter roles based on company selection and search term
  const filteredRoles = useMemo(() => {
    let data = roles.data;

    if (isMultiCompany && selectedCompanyUuid) {
      data = data.filter((role) => role.company_uuid === selectedCompanyUuid);
    }

    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((role) => {
      return (
        role.name?.toLowerCase().includes(searchLower) ||
        role.description?.toLowerCase().includes(searchLower)
      );
    });
  }, [roles.data, searchTerm, isMultiCompany, selectedCompanyUuid]);

  // Group identical roles across companies
  const groupedRoles = useMemo(() => {
    return groupRoles(filteredRoles, companies.data || {});
  }, [filteredRoles, companies.data]);

  // Sort roles
  const sortedRoles = useMemo(() => {
    if (!sortConfig.key) return groupedRoles;

    const sorted = [...groupedRoles].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (typeof aValue === "boolean") {
        return sortConfig.direction === "asc"
          ? aValue === bValue
            ? 0
            : aValue
            ? -1
            : 1
          : aValue === bValue
          ? 0
          : aValue
          ? 1
          : -1;
      }

      return 0;
    });

    return sorted;
  }, [groupedRoles, sortConfig]);

  // Paginate roles
  const paginatedRoles = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    const endIndex = startIndex + paginationConfig.pageSize;
    return sortedRoles.slice(startIndex, endIndex);
  }, [sortedRoles, paginationConfig]);

  // Reset pagination when filters change
  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [groupedRoles.length]);

  // Handle sort
  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle delete
  const handleDelete = async (role, event) => {
    event.stopPropagation();

    // Check if role has users associated with it
    const userCount = role.totalUsers || 0;
    if (userCount > 0) {
      alert(
        "This role can't be deleted because it's currently assigned to one or more users. Please reassign those users to a different role before deleting this one."
      );
      return;
    }

    const isGrouped = role.uuids && role.uuids.length > 1;
    const confirmMessage = isGrouped
      ? `Are you sure you want to delete the role "${
          role.name
        }" from all companies (${role.companyNames?.join(
          ", "
        )})?\n\nThis action cannot be undone.`
      : `Are you sure you want to delete the role "${role.name}"?\n\nThis action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      const uuidsToDelete = role.uuids || [role.uuid];
      for (const uuid of uuidsToDelete) {
        const result = await deleteRole(uuid);
        if (!result.success) {
          alert(`Failed to delete role: ${result.error}`);
          break;
        }
      }
    }
  };

  // Table columns configuration
  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      width: "180px",
      render: (role, value) => (
        <span
          style={{
            fontWeight: 500,
            color: "rgba(46, 50, 66, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "180px",
          }}
          title={value || ""}
        >
          {value}
        </span>
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      width: "250px",
      render: (role, value) => (
        <span
          style={{
            color: "rgba(100, 116, 139, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "250px",
          }}
          title={value || ""}
        >
          {value || "-"}
        </span>
      ),
    },
    {
      key: "companyNames",
      label: "Companies",
      sortable: false,
      width: "200px",
      render: (role) =>
        role.companyNames?.length > 0 ? (
          <div className="role-company-tags">
            {role.companyNames.map((name, i) => (
              <span key={i} className="role-company-tag" title={name}>
                {name}
              </span>
            ))}
          </div>
        ) : (
          "—"
        ),
    },
    {
      key: "activeUsers",
      label: "Active Users",
      sortable: true,
      width: "110px",
      align: "center",
      render: (role, value) => value || 0,
    },
    {
      key: "totalUsers",
      label: "Total Users",
      sortable: true,
      width: "110px",
      align: "center",
      render: (role, value) => value || 0,
    },
    {
      key: "enableTime",
      label: "Time",
      width: "80px",
      align: "center",
      render: (role, value) => (
        <span
          className={`role-badge ${
            value ? "role-badge--enabled" : "role-badge--disabled"
          }`}
        >
          {value ? "✓" : "✗"}
        </span>
      ),
    },
    {
      key: "enableExpense",
      label: "Expense",
      width: "80px",
      align: "center",
      render: (role, value) => (
        <span
          className={`role-badge ${
            value ? "role-badge--enabled" : "role-badge--disabled"
          }`}
        >
          {value ? "✓" : "✗"}
        </span>
      ),
    },
    {
      key: "enableProjectManagement",
      label: "Project Mgmt",
      width: "120px",
      align: "center",
      render: (role, value) => (
        <span
          className={`role-badge ${
            value ? "role-badge--enabled" : "role-badge--disabled"
          }`}
        >
          {value ? "✓" : "✗"}
        </span>
      ),
    },
    ...(SERVICE_MANAGEMENT_ENABLED
      ? [
          {
            key: "enableServiceManagement",
            label: "Service Mgmt",
            width: "120px",
            align: "center",
            render: (role, value) => (
              <span
                className={`role-badge ${
                  value ? "role-badge--enabled" : "role-badge--disabled"
                }`}
              >
                {value ? "✓" : "✗"}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "created",
      label: "Created",
      sortable: true,
      width: "120px",
      render: (role, value) => {
        if (!value) return "-";
        try {
          const date = new Date(value);
          return date.toLocaleDateString();
        } catch {
          return value;
        }
      },
    },
  ];

  // Export columns configuration
  const exportColumns = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    {
      key: "companyNames",
      label: "Companies",
      format: (val) => (val || []).join(", "),
    },
    { key: "activeUsers", label: "Active Users", format: (val) => val || 0 },
    { key: "totalUsers", label: "Total Users", format: (val) => val || 0 },
    {
      key: "enableTime",
      label: "Time Enabled",
      format: (val) => (val ? "Yes" : "No"),
    },
    {
      key: "enableExpense",
      label: "Expense Enabled",
      format: (val) => (val ? "Yes" : "No"),
    },
    {
      key: "enableProjectManagement",
      label: "Project Management Enabled",
      format: (val) => (val ? "Yes" : "No"),
    },
    ...(SERVICE_MANAGEMENT_ENABLED
      ? [
          {
            key: "enableServiceManagement",
            label: "Service Management Enabled",
            format: (val) => (val ? "Yes" : "No"),
          },
        ]
      : []),
    {
      key: "created",
      label: "Created",
      format: (val) => {
        if (!val) return "";
        try {
          return new Date(val).toLocaleDateString();
        } catch {
          return val;
        }
      },
    },
  ];

  // Table actions
  const actions = [
    {
      label: "Delete",
      icon: Trash2,
      onClick: handleDelete,
      className: "action-button-delete",
    },
  ];

  return (
    <div className="roles-list">
      {/* Header */}
      <div className="roles-list__header">
        <div className="roles-list__header-top">
          <div className="roles-list__header-left">
            <h2 className="roles-list__title">Roles</h2>
            {isMultiCompany && (
              <div className="roles-list__company-filter">
                <Building2
                  size={18}
                  style={{ color: "rgba(100, 116, 139, 1)", flexShrink: 0 }}
                />
                <select
                  className="roles-list__company-select"
                  value={selectedCompanyUuid}
                  onChange={handleCompanyChange}
                  title={
                    companyOptions.find(
                      (opt) => opt.value === selectedCompanyUuid
                    )?.label || "None selected"
                  }
                >
                  <option value="">None selected</option>
                  {companyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search roles..."
          />
          <div className="roles-list__actions">
            <ExportButton
              data={sortedRoles}
              filename="roles_export"
              columns={exportColumns}
              className="button--secondary"
            />
            <Button
              className="button--secondary"
              onClick={() => fetchRoles(true)}
              disabled={roles.loading}
              title="Refresh roles"
            >
              <RefreshCw size={18} style={{ marginRight: "6px" }} />
              Refresh
            </Button>
            <Button className="button--primary" onClick={onAddRole}>
              <Plus size={18} style={{ marginRight: "6px" }} />
              Add Role
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {roles.loading && (
        <div className="roles-list__loading">
          <p>Loading roles...</p>
        </div>
      )}

      {/* Error State */}
      {roles.error && (
        <div className="roles-list__error">
          <p>Error loading roles: {roles.error}</p>
          <Button onClick={() => fetchRoles(true)}>Retry</Button>
        </div>
      )}

      {/* Table */}
      {!roles.loading && !roles.error && (
        <>
          <SortableTable
            data={paginatedRoles}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={onRoleSelect}
            actions={actions}
            emptyMessage="No roles found. Click 'Add Role' to create your first role."
          />
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={sortedRoles.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}
    </div>
  );
}
